import { Page } from "@mb-pro-ui/components";
import { Select, Autocomplete } from "@mb-pro-ui/components/form";
import { Grid, Button, Skeleton } from "@mui/material";
import { Form } from "react-final-form";
import { FormatDateOptions, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

import { Firmware, SoftwareType } from "../utils/types";
import { useApi } from "../utils/ApiProvider";
import useEditedStyles from "../hooks/useEditedStyles";
import TitledCard from "../components/templates/basic/TitledCard";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { ConfirmDialog, ConfirmDialogProps } from "../components/ConfirmDialog";
import { useFirmwareProperties } from "../hooks/useFirmwareProperties";
import B64FileField from "../components/B64FileField";
import { FirmwareLinkField } from "../components/FirmwareLinkField";

import type { paths } from "@helios/toolbox-types";
import { usePageSxProps } from "./styles";
import { TextField } from "../components/form/TextField";
import { APP_NAME } from "../App";
type CreateFileResponse = paths["/firmwares"]["post"]["responses"]["200"]["content"]["application/json"];

const SwverValidator = (value: any): undefined | string => {
  if (typeof value === "number" && !isNaN(value) && (value < 0 || value > 255)) {
    return "0-255";
  }
  return undefined;
};

const FirmwarePage = () => {
  const [dialogProps, setDialogProps] = useState<Omit<ConfirmDialogProps, "open"> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { id } = useParams<{ id: string }>();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage, formatDate } = useIntl();
  const api = useApi();
  const editedStyle = useEditedStyles();
  const history = useHistory();
  const { closeSnackbar } = useSnackbar();
  const { statusValues, urgencyValues } = useFirmwareProperties();

  const dateFormatOptions: FormatDateOptions = useMemo(
    () => ({
      dateStyle: "short",
      timeStyle: "medium",
    }),
    []
  );

  const statusItems = useMemo(() => Array.from(statusValues.entries()).map(([value, label]) => ({ value, label })), [statusValues]);
  const urgencyItems = useMemo(() => Array.from(urgencyValues.entries()).map(([value, label]) => ({ value, label })), [urgencyValues]);

  const isNew = useMemo(() => id === "new", [id]);

  // TODO: további kezelt hibák felsorolása!!!
  const { errorHandler: onError } = useErrorHandler<Firmware>([
    [
      { status: 401, type: "firmwares" },
      () => formatMessage({ defaultMessage: "You have no rights for firmware management." }),
      () => {
        history.push("/");
      },
    ],
    [{ status: 404 }, () => formatMessage({ defaultMessage: "Firmware not found." }), () => history.push("/firmwares")],
  ]);

  const firmwareQuery = () => api<Firmware>(`/firmwares/${id}`);
  const hwtypesQuery = () => api<string[]>("/units/hwtypes");
  const swtypesQuery = () => api<SoftwareType[]>("/firmwares/used-swtypes");

  const saveQuery = useCallback(
    (file: Firmware) => {
      closeSnackbar();
      if (isNew) {
        const { id: _id, ...newFile } = file;
        return api(`/firmwares`, { method: "post", body: JSON.stringify(newFile) });
      }
      return api(`/firmwares/${id}`, { method: "post", body: JSON.stringify(file) });
    },
    [api, closeSnackbar, id, isNew]
  );
  const deleteQuery = () => api(`/remote-files/${id}`, { method: "delete" });

  const { mutate: getFileData, data: file, isLoading: firmwareIsLoading } = useMutation({ mutationFn: firmwareQuery, onError });
  const { mutate: getHwtypes, data: hwtypes, isLoading: hwtypesAreLoading } = useMutation({ mutationFn: hwtypesQuery, onError });
  const { mutate: getUsedSwtypes, data: usedSwtypes, isLoading: swtypesAreLoading } = useMutation({ mutationFn: swtypesQuery, onError });

  const loading = useMemo(() => firmwareIsLoading || hwtypesAreLoading || swtypesAreLoading, [firmwareIsLoading, hwtypesAreLoading, swtypesAreLoading]);
  const swtypes = useMemo(
    () => usedSwtypes?.sort((a, b) => a.value.toLocaleLowerCase().localeCompare(b.value.toLocaleLowerCase()) || b.count - a.count).map((item) => item.value),
    [usedSwtypes]
  );

  const onSaveSuccess = useCallback(
    (response: any) => {
      if (isNew) {
        const { id } = response as CreateFileResponse;
        history.push(`/firmwares/${id}`);
        return;
      }
      getFileData();
    },
    [getFileData, history, isNew]
  );
  const { mutate: saveFirmware, isLoading: saving } = useMutation({ mutationFn: saveQuery, onSuccess: onSaveSuccess, onError });
  const { mutate: deleteFirmware, isLoading: deleting } = useMutation({
    mutationFn: deleteQuery,
    onSuccess: () => {
      history.push("/firmwares");
    },
    onError,
  });

  const saveAndDeleteDisabled = useMemo(() => saving || deleting, [saving, deleting]);

  const askRemoveFile = useCallback(() => {
    setDialogProps({
      title: formatMessage({ defaultMessage: "Delete firmware" }),
      message: formatMessage({ defaultMessage: "Are you sure to delete this firmware?" }),
      onClose: () => setDialogOpen(false),
      okAction: () => {
        setDialogOpen(false);
        deleteFirmware();
      },
      okLabel: formatMessage({ defaultMessage: "Delete" }),
      color: "error",
    });
    setDialogOpen(true);
  }, [deleteFirmware, formatMessage]);

  const onSubmit = useCallback(
    async (args: Firmware) => {
      saveFirmware(args);
    },
    [saveFirmware]
  );

  const numberParse = useCallback((value: string) => {
    return value === "" ? null : parseInt(value);
  }, []);

  const numberFormat = useCallback((value: unknown) => {
    return value === null || value === undefined || (typeof value === "number" && isNaN(value)) ? "" : `${value}`;
  }, []);

  const formatTsValue = useCallback(
    (ts: unknown) => {
      if (typeof ts !== "string" && typeof ts !== "number" && !(ts instanceof Date)) {
        return "";
      }
      return formatDate(ts, dateFormatOptions);
    },
    [dateFormatOptions, formatDate]
  );

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Firmwares" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    const unregister = history.listen(() => {
      closeSnackbar();
    });

    return () => {
      unregister();
    };
  }, [history, closeSnackbar]);

  useEffect(() => {
    if (isNew) {
      return;
    }
    getFileData();
  }, [getFileData, isNew]);

  useEffect(() => {
    getHwtypes();
  }, [getHwtypes]);

  useEffect(() => {
    getUsedSwtypes();
  }, [getUsedSwtypes]);

  return (
    <Page sx={singlePageSx}>
      <Form<Firmware>
        onSubmit={onSubmit}
        initialValues={file}
        render={({ handleSubmit, dirty }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item sx={{ flexGrow: 1 }}></Grid>
              {isNew ? null : (
                <Grid item>
                  <Button color="inherit" startIcon={<DeleteIcon />} sx={{ color: "error.main" }} onClick={askRemoveFile} disabled={saveAndDeleteDisabled}>
                    {formatMessage({ defaultMessage: "Remove" })}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button
                  color="inherit"
                  startIcon={<SaveOutlinedIcon />}
                  className={dirty ? editedStyle.withHover : ""}
                  disabled={saveAndDeleteDisabled}
                  type="submit"
                >
                  {formatMessage({ defaultMessage: "Save" })}
                </Button>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex" }}>
                <TitledCard headerSx={{ display: "none" }} stretch={true}>
                  <Grid container spacing={1} sx={{ p: 1 }}>
                    {loading ? (
                      <>
                        <Grid item xs={12} md={8}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} md={8}>
                          <Autocomplete
                            name="hwtype"
                            size="small"
                            required
                            label={formatMessage({ defaultMessage: "Hardware type" })}
                            options={hwtypes ?? []}
                            fullWidth
                            sx={{ ".MuiFormControl-root": { m: 0 } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            name="hwver"
                            required
                            label={formatMessage({ defaultMessage: "Hardware version" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0, minWidth: 50 }}
                            parse={numberParse}
                            format={numberFormat}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Autocomplete
                            name="swtype"
                            size="small"
                            required
                            label={formatMessage({ defaultMessage: "Software type" })}
                            options={swtypes ?? []}
                            freeSolo
                            fullWidth
                            sx={{ ".MuiFormControl-root": { m: 0 } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            name="swver"
                            label={formatMessage({ defaultMessage: "Software version" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0, minWidth: 50 }}
                            parse={numberParse}
                            format={numberFormat}
                            validate={SwverValidator}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Select
                            name="status"
                            required
                            label={formatMessage({ defaultMessage: "Status" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            outerSx={{ m: 0 }}
                            items={statusItems}
                            renderOption={({ option }) => option.label}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Select
                            name="urgency"
                            required
                            label={formatMessage({ defaultMessage: "Urgency" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            outerSx={{ m: 0 }}
                            items={urgencyItems}
                            renderOption={({ option }) => option.label}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TitledCard>
              </Grid>
              <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex" }}>
                <TitledCard headerSx={{ display: "none" }} stretch={true}>
                  <Grid container spacing={1} sx={{ p: 1 }}>
                    {loading ? (
                      <>
                        <Grid item xs={12} md={8}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                        <Grid item xs={12}>
                          <Skeleton variant="rounded" height={46.56} />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} md={8}>
                          <TextField
                            name="description"
                            required
                            label={formatMessage({ defaultMessage: "Description" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            name="version"
                            required
                            label={formatMessage({ defaultMessage: "Version" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0, minWidth: 50 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            name="repository"
                            label={formatMessage({ defaultMessage: "Repository" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            name="commit"
                            label={formatMessage({ defaultMessage: "Commit hash" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FirmwareLinkField />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="last_changed_ts"
                            readOnly
                            label={formatMessage({ defaultMessage: "Last changed" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0 }}
                            format={formatTsValue}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TitledCard>
              </Grid>
              {isNew ? (
                <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex" }}>
                  <TitledCard headerSx={{ display: "none" }} stretch={true}>
                    <Grid container spacing={1} sx={{ p: 1, flex: 1 }}>
                      {loading ? (
                        <Grid item xs={12} sx={{ flex: 1 }}>
                          <Skeleton variant="rounded" sx={{ minHeight: 120, height: 1 / 1 }} />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <B64FileField name="firmware" sx={{ minHeight: 120, height: 1 / 1 }} />
                        </Grid>
                      )}
                    </Grid>
                  </TitledCard>
                </Grid>
              ) : null}
            </Grid>
          </form>
        )}
      />
      {dialogProps ? <ConfirmDialog open={dialogOpen} {...dialogProps} /> : null}
    </Page>
  );
};

export default FirmwarePage;
